import React, { useState, useEffect } from 'react';
import { speakTextWithBestQuality, speakTextWithFallback, isMobileSafari, isMobile } from '../utils/voiceManager';
import { voiceQualityOptimizer } from '../utils/voiceQualityOptimizer';

const VoiceQualityTester = ({ onBack }) => {
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [testText, setTestText] = useState("Hello! I'm your English tutor. How can I help you today?");
  const [isPlaying, setIsPlaying] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [optimizationInfo, setOptimizationInfo] = useState({});

  useEffect(() => {
    // Load available voices
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        setAvailableVoices(voices);
        if (voices.length > 0) {
          setSelectedVoice(voices[0]);
        }
      };

      if (window.speechSynthesis.getVoices().length > 0) {
        loadVoices();
      } else {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
    }

    // Get device and optimization information
    setDeviceInfo({
      isMobileSafari: isMobileSafari(),
      isMobile: isMobile(),
      userAgent: navigator.userAgent,
      platform: navigator.platform
    });

    setOptimizationInfo({
      voiceQuality: voiceQualityOptimizer.voiceQuality,
      optimizationLevel: voiceQualityOptimizer.optimizationLevel,
      recommendations: voiceQualityOptimizer.getOptimizationRecommendations(),
      settings: voiceQualityOptimizer.getOptimizedVoiceSettings()
    });
  }, []);

  const testVoice = (useOptimizer = true) => {
    if (isPlaying) return;

    const onStart = () => setIsPlaying(true);
    const onEnd = () => setIsPlaying(false);
    const onError = (error) => {
      console.error('Voice test error:', error);
      setIsPlaying(false);
    };

    if (useOptimizer) {
      speakTextWithBestQuality(testText, availableVoices, onStart, onEnd, onError);
    } else {
      speakTextWithFallback(testText, availableVoices, onStart, onEnd, onError);
    }
  };

  const stopVoice = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-900 text-white">
      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="mb-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white flex items-center"
        >
          ← Back
        </button>
      )}
      
      <h1 className="text-3xl font-bold mb-6 text-center">Voice Quality Tester</h1>
      
      {/* Device Information */}
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-3">Device Information</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Mobile Safari:</span> {deviceInfo.isMobileSafari ? 'Yes' : 'No'}
          </div>
          <div>
            <span className="font-medium">Mobile Device:</span> {deviceInfo.isMobile ? 'Yes' : 'No'}
          </div>
          <div>
            <span className="font-medium">Platform:</span> {deviceInfo.platform}
          </div>
          <div>
            <span className="font-medium">Voice Quality:</span> {optimizationInfo.voiceQuality}
          </div>
        </div>
      </div>

      {/* Optimization Information */}
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-3">Optimization Settings</h2>
        <div className="mb-3">
          <span className="font-medium">Optimization Level:</span> {optimizationInfo.optimizationLevel}
        </div>
        <div className="mb-3">
          <span className="font-medium">Voice Settings:</span>
          <pre className="bg-gray-700 p-2 rounded mt-2 text-xs">
            {JSON.stringify(optimizationInfo.settings, null, 2)}
          </pre>
        </div>
        <div>
          <span className="font-medium">Recommendations:</span>
          <ul className="list-disc list-inside mt-2 text-sm">
            {optimizationInfo.recommendations?.map((rec, index) => (
              <li key={index} className="text-gray-300">{rec}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Voice Selection */}
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-3">Available Voices</h2>
        <select 
          value={selectedVoice?.name || ''} 
          onChange={(e) => {
            const voice = availableVoices.find(v => v.name === e.target.value);
            setSelectedVoice(voice);
          }}
          className="w-full p-2 bg-gray-700 rounded text-white"
        >
          {availableVoices.map((voice, index) => (
            <option key={index} value={voice.name}>
              {voice.name} ({voice.lang}) - {voice.localService ? 'Local' : 'Remote'}
            </option>
          ))}
        </select>
      </div>

      {/* Test Controls */}
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-3">Test Controls</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Test Text:</label>
          <textarea
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            className="w-full p-2 bg-gray-700 rounded text-white"
            rows={3}
          />
        </div>
        
        <div className="flex gap-4">
          <button
            onClick={() => testVoice(true)}
            disabled={isPlaying}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded"
          >
            Test with Optimizer
          </button>
          
          <button
            onClick={() => testVoice(false)}
            disabled={isPlaying}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded"
          >
            Test without Optimizer
          </button>
          
          <button
            onClick={stopVoice}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded"
          >
            Stop
          </button>
        </div>
        
        {isPlaying && (
          <div className="mt-4 p-2 bg-blue-900 rounded text-center">
            Playing voice... {selectedVoice?.name}
          </div>
        )}
      </div>

      {/* Voice Details */}
      {selectedVoice && (
        <div className="bg-gray-800 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-3">Selected Voice Details</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Name:</span> {selectedVoice.name}
            </div>
            <div>
              <span className="font-medium">Language:</span> {selectedVoice.lang}
            </div>
            <div>
              <span className="font-medium">Local Service:</span> {selectedVoice.localService ? 'Yes' : 'No'}
            </div>
            <div>
              <span className="font-medium">Default:</span> {selectedVoice.default ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceQualityTester;
